/**
 * Entity Profile 주소 조회 컴포저블.
 * L_ENTITY_PROFILE 의 5개 전용 주소 컬럼을 /api/setupEntity/changeAddress/address-info 를 통해 반환.
 *
 * Preset 챗봇에서 Entity 선택 시 이 함수 하나로 주소 데이터를 일괄 로드한다.
 */

export type EntityAddressType = 'registered' | 'business' | 'membersKept' | 'financialKept' | 'branch';

export interface EntityAddressOption {
  type: EntityAddressType;
  label: string;
  value: string;
  structured?: Record<string, any>;
}

export interface EntityAddressResult {
  /** 5개 컬럼에서 추출된 주소 목록 (비어있는 주소 제외) */
  addresses: EntityAddressOption[];
  /** 등록지 주소 composed string */
  registered: string;
  /** 사업장 주소 composed string */
  business: string;
  /** 회원명부 보관 주소 */
  membersKept: string;
  /** 재무기록 보관 주소 */
  financialKept: string;
  /** 지점 주소 */
  branch: string;
  // 기본 entity 정보 (backward compat)
  companyName: string;
  regNo: string;
  /** 구 등록번호 (REG_NO_OLD) — 문서의 [[old_registration_no]] 용. 없으면 '' */
  regNoOld: string;
  clauseNo: string;
  articleNo: string;
  registeredOfficeAddress?: {
    composed: string;
    structured?: Record<string, any>;
  };
}

/**
 * MY 회사등록번호 표시: 신번호 (구번호) — 구번호 없으면 신번호만.
 * 예: "202001234567 (1234567-A)"
 */
export function formatMyCompanyRegNo(regNo?: string | null, regNoOld?: string | null): string {
  const reg = String(regNo || '').trim();
  const old = String(regNoOld || '').trim();
  if (!reg) return old;
  if (old && old !== reg) return `${reg} (${old})`;
  return reg;
}

/**
 * Entity 주소 정보 일괄 조회.
 * L_ENTITY_PROFILE 의 REGISTERED / BUSINESS / MEMBERS_KEPT / FINANTIAL_KEPT / BRANCH
 * 5개 전용 컬럼을 파싱해 반환.
 *
 * @param entityCd - L_ENTITY.ENTITY_CD
 * @returns EntityAddressResult 또는 null (조회 실패)
 */
export async function fetchEntityAddresses(entityCd: string): Promise<EntityAddressResult | null> {
  if (!entityCd) return null;
  try {
    const res = await fetch(
      `/api/setupEntity/changeAddress/address-info/${encodeURIComponent(entityCd)}`,
      { credentials: 'include' }
    );
    if (!res.ok) return null;
    const json = await res.json();
    if (!json?.success) return null;

    const addresses: EntityAddressOption[] = Array.isArray(json.addresses) ? json.addresses : [];

    const pick = (type: EntityAddressType) =>
      addresses.find((a) => a.type === type)?.value || '';

    return {
      addresses,
      registered: pick('registered') || json.registeredOfficeAddress?.composed || '',
      business: pick('business'),
      membersKept: pick('membersKept'),
      financialKept: pick('financialKept'),
      branch: pick('branch'),
      companyName: json.companyName || '',
      regNo: json.regNo || '',
      regNoOld: json.regNoOld || '',
      clauseNo: json.clauseNo || '',
      articleNo: json.articleNo || '',
      registeredOfficeAddress: json.registeredOfficeAddress,
    };
  } catch (err) {
    console.warn('[useEntityAddresses] fetch failed:', err);
    return null;
  }
}
